import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteRole,
    getRoles,
    updateRoleStatus,
} from '../store/roles/actions';
import { capitalizeFirst } from '../commonUtils/string.helper';
import { hasPermission } from '../commonUtils/permission.helper';
import constants from '../commonUtils/constants';
import { Switch } from "../components/ui/switch";
import IconButton from '../components/IconButton';
import { EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

const RolesContext = createContext();

export const useRoles = () => useContext(RolesContext);

export const RolesProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { filters, RolesList, loading, errors } = useSelector((state) => state.Roles);

    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [updateData, setUpdateData] = useState({ _id: "", title: "", role: "" });

    const debouncedGetRoles = useMemo(() => debounce((params) => {
        dispatch(getRoles(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetRoles({
            currentPage: 1,
            perPage: RolesList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, RolesList?.pagination?.perPage, debouncedGetRoles]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
    }, [RolesList]);

    const roles = useMemo(() => RolesList?.data?.map(
        ({ _id, key, title, role, status }, index) => ({
            id: index + 1,
            _id,
            key,
            role,
            title,
            status: capitalizeFirst(status),
        })
    ), [RolesList]);

    const pagination = useMemo(() => RolesList?.pagination, [RolesList]);
    
    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetRoles({ currentPage, perPage, ...filters });
    }, [debouncedGetRoles, filters]);

    const manageRolePermission = useCallback((key) => navigate(`/roles/permissions/${key}`), [navigate]);
    const editRole = useCallback((data) => setUpdateData(data), []);
    const handleToggleStatus = useCallback((id) => dispatch(updateRoleStatus(id)), [dispatch]);
    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const columnHelper = useMemo(() => createColumnHelper(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('role', { header: 'Code', cell: info => info.getValue() }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => (
                <Switch
                    checked={info.getValue() === 'Active'}
                    onCheckedChange={() => handleToggleStatus(info.row.original._id)}
                    disabled={!hasPermission('roles.update')}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    {hasPermission('roles.permissions_view') && (
                        <IconButton
                            variant="action_default"
                            className="ml-2"
                            size="icon"
                            Icon={EyeIcon}
                            onClick={() => manageRolePermission(info.row.original.key)}
                        />
                    )}
                    {hasPermission('roles.update') && (
                        <IconButton variant="action_default" size="icon" Icon={PencilSquareIcon} onClick={() => editRole(info.row.original)} />
                    )}
                    {hasPermission('roles.create_delete') && (
                        <IconButton variant="action_default" size="icon" Icon={TrashIcon} onClick={() => askDeleteConfirmation(info.row.original._id)} />
                    )}
                </>
            ),
        }),
    ], [columnHelper, handleToggleStatus, manageRolePermission, editRole, askDeleteConfirmation]);

    const table = useReactTable({
        data: roles,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: RolesList?.pagination?.currentPage - 1,
                pageSize: RolesList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: RolesList?.pagination?.totalPages,
    });

    const handleCloseModal = (action) => {
        if(action === "update"){
            setUpdateData({id:"", title:"", role: ""})
            setOpenUpdateModal(false);
        } else if(action === "delete") {
            setToDeleteId("");
            setShowDeleteConfirmationDialog(false);
        }
    }
    const handleDelete = () => {
        dispatch(deleteRole(toDeleteId));
        handleCloseModal('delete');
    };

    return (
        <RolesContext.Provider value={{
            roles,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editRole,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
        }}>
            {children}
        </RolesContext.Provider>
    );
};
